import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { HttpErrorResponse, HttpEventType } from '@angular/common/http';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription, interval, of } from 'rxjs';
import { catchError, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { PersonCreditsService } from 'src/app/services/person-credits.service';
import { UploadAsyncJobModel } from 'src/app/model/upload-async-job.model';
import { extractUploadErrorMessages } from 'src/app/utils/upload-error.util';
import { downloadBlobFile } from 'src/app/utils/download-file.util';

const ACTIVE_JOB_STORAGE_KEY = 'activePersonCreditsImportJobId';
const POLL_INTERVAL_MS = 1000;
const ACTIVE_STATUSES = ['QUEUED', 'VALIDATING', 'PROCESSING'];

@Component({
  selector: 'app-cat-person-credits',
  templateUrl: './cat-person-credits.component.html',
  styleUrls: ['./cat-person-credits.component.css']
})
export class CatPersonCreditsComponent implements OnInit, OnDestroy {
  public viewStatus = 0;
  public personCreditsList: any[] = [];

  @ViewChild('fileInput', { static: false }) fileInputRef: ElementRef;
  public uploadFile: File = null;
  public replaceAll = false;

  // subida HTTP del archivo (fase previa a que exista jobId)
  public isUploadingFile = false;
  public uploadHttpPercent = 0;

  // trabajo de importación async en curso (o el último conocido)
  public activeJob: UploadAsyncJobModel = null;
  private pollingSub: Subscription = null;
  private destroy$ = new Subject<void>();

  dtOptions = {
    order: [],
    responsive: true,
    columnDefs: [
      { responsivePriority: 1, width: 100, targets: -1 }
    ],
    bLengthChange: true,
    searching: true,
    dom: '<"row"<"col"l><"col"<"text-center"B>><"col"f>><"row"<"col"t>><"row"<"col"i><"col"p>>',
    buttons: ['csv', 'excel'],
    autoWidth: false,
    paging: true,
    pageLength: 100,
    language: {
      processing: 'Procesando...',
      search: 'Buscar:',
      lengthMenu: 'Mostrar _MENU_ elementos',
      info: 'Mostrando del _START_ al _END_ de _TOTAL_ elementos',
      infoEmpty: 'Mostrando ningún elemento.',
      infoFiltered: '(filtrado _MAX_ elementos total)',
      infoPostFix: '',
      loadingRecords: 'Cargando registros...',
      zeroRecords: 'No se encontraron registros',
      emptyTable: 'No hay datos disponibles en la tabla',
      paginate: {
        first: 'Primero',
        previous: 'Anterior',
        next: 'Siguiente',
        last: 'Último'
      },
      aria: {
        sortAscending: ': Activar para ordenar la tabla en orden ascendente',
        sortDescending: ': Activar para ordenar la tabla en orden descendente'
      },
      buttons: ['excel']
    }
  };

  constructor(
    private personCreditsService: PersonCreditsService,
    private toastr: ToastrService
  ) { }

  ngOnInit() {
    this.getPersonCredits();

    // si quedó un job activo de una navegación anterior, recuperar su progreso de inmediato
    const savedJobId = localStorage.getItem(ACTIVE_JOB_STORAGE_KEY);
    if (savedJobId) {
      this.startPolling(savedJobId);
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFileSelected = (event: any) => {
    this.uploadFile = event.target.files.length > 0 ? event.target.files[0] : null;
  }

  // el botón de carga se deshabilita mientras se sube el archivo o mientras exista un job activo
  isUploadBusy = (): boolean => {
    return this.isUploadingFile || (this.activeJob && ACTIVE_STATUSES.indexOf(this.activeJob.status) !== -1);
  }

  uploadExcel = () => {
    if (!this.uploadFile) {
      this.toastr.warning('Selecciona un archivo Excel primero.');
      return;
    }
    if (this.isUploadBusy()) {
      this.toastr.warning('Ya existe una importación en curso. Espera a que finalice.');
      return;
    }

    let userName = localStorage.getItem('com_userName');
    this.isUploadingFile = true;
    this.uploadHttpPercent = 0;
    this.activeJob = null;

    this.personCreditsService.uploadAsync(userName, this.uploadFile, this.replaceAll)
      .pipe(
        takeUntil(this.destroy$),
        tap((event: any) => {
          if (event.type === HttpEventType.UploadProgress && event.total) {
            this.uploadHttpPercent = Math.round((event.loaded / event.total) * 100);
          } else if (event.type === HttpEventType.Response) {
            const resp: any = event.body;
            this.isUploadingFile = false;
            if (resp && (resp.codeHttpStatus === 202 || resp.codeHttpStatus === 200) && resp.data && resp.data.jobId) {
              this.resetFileInput();
              localStorage.setItem(ACTIVE_JOB_STORAGE_KEY, resp.data.jobId);
              this.activeJob = resp.data;
              this.startPolling(resp.data.jobId);
            } else {
              const messages = extractUploadErrorMessages(resp);
              this.toastr.error(messages.join('<br>'), '¡Atención!', { enableHtml: true });
            }
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.isUploadingFile = false;
          this.uploadHttpPercent = 0;

          if (error && error.status === 409) {
            // ya existe una importación activa: recuperar/mantener el seguimiento del trabajo actual en lugar de iniciar otra
            const existingJobId = error.error && error.error.data && error.error.data.jobId;
            if (existingJobId) {
              localStorage.setItem(ACTIVE_JOB_STORAGE_KEY, existingJobId);
              this.startPolling(existingJobId);
            }
            const savedJobId = localStorage.getItem(ACTIVE_JOB_STORAGE_KEY);
            if (!existingJobId && savedJobId) {
              this.startPolling(savedJobId);
            }
            this.toastr.warning(extractUploadErrorMessages(error).join('<br>') || 'Ya existe una importación activa para este catálogo.', '¡Atención!', { enableHtml: true });
          } else if (error && error.status === 400) {
            const messages = extractUploadErrorMessages(error);
            this.toastr.error(messages.join('<br>'), '¡Atención!', { enableHtml: true });
          } else {
            this.toastr.error('Ocurrió un error al intentar iniciar la importación. Puedes reintentar.', '¡Atención!');
          }
          return of(null);
        })
      )
      .subscribe();
  }

  // POLLING del estado del job cada 1s
  startPolling = (jobId: string) => {
    this.stopPolling();
    this.pollingSub = interval(POLL_INTERVAL_MS)
      .pipe(
        startWith(0),
        switchMap(() => this.personCreditsService.getUploadStatus(jobId)
          .pipe(
            catchError(() => of(null)) // errores transitorios de red: se ignora este tick y se reintenta en el siguiente
          )
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((resp: any) => this.handleJobUpdate(resp));
  }

  stopPolling = () => {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
      this.pollingSub = null;
    }
  }

  handleJobUpdate = (resp: any) => {
    if (!resp || !resp.data) return;
    this.activeJob = resp.data;

    if (this.activeJob.status === 'COMPLETED') {
      this.stopPolling();
      localStorage.removeItem(ACTIVE_JOB_STORAGE_KEY);
      this.toastr.success(this.activeJob.message || 'La importación se completó correctamente.');
      this.getPersonCredits();
    } else if (this.activeJob.status === 'FAILED') {
      this.stopPolling();
      // el jobId se limpia hasta que el usuario cierre el error (ver closeJobError), para no perder el seguimiento si navega y regresa
    }
  }

  // el usuario cierra el banner de error: ahora sí se limpia el job y puede seleccionar/cargar de nuevo
  closeJobError = () => {
    localStorage.removeItem(ACTIVE_JOB_STORAGE_KEY);
    this.activeJob = null;
  }

  closeJobSuccess = () => {
    this.activeJob = null;
  }

  jobStatusLabel = (): string => {
    if (!this.activeJob) return '';
    switch (this.activeJob.status) {
      case 'QUEUED': return 'Importación en cola';
      case 'VALIDATING': return 'Validando archivo y catálogos...';
      case 'PROCESSING': return `Procesando ${this.activeJob.processedRecords || 0} de ${this.activeJob.totalRecords || 0} registros (${this.activeJob.progressPercent || 0}%)`;
      case 'COMPLETED': return this.activeJob.message || 'La importación se completó correctamente.';
      case 'FAILED': return this.activeJob.errorMessage || 'La importación falló.';
      default: return '';
    }
  }

  // barra indeterminada mientras se sube el archivo, o durante VALIDATING sin total de registros aún
  isProgressIndeterminate = (): boolean => {
    if (this.isUploadingFile) return true;
    if (this.activeJob && this.activeJob.status === 'VALIDATING' && !this.activeJob.totalRecords) return true;
    return false;
  }

  resetFileInput = () => {
    this.uploadFile = null;
    if (this.fileInputRef) this.fileInputRef.nativeElement.value = '';
  }

  downloadLayout = () => {
    this.personCreditsService.downloadLayout().subscribe(
      (data: Blob) => downloadBlobFile(data, 'layout_personas_creditos.xlsx'),
      () => this.toastr.error('No se pudo descargar el layout.')
    );
  }

  getPersonCredits = () => {
    this.personCreditsService.getAll()
      .pipe(
        takeUntil(this.destroy$),
        tap((resp: any) => {
          this.personCreditsList = resp.data || [];
          this.viewStatus = 1;
        }),
        catchError(() => {
          this.viewStatus = -1;
          this.toastr.error('Ocurrió un error al leer el catálogo de personas y créditos.');
          return of(null);
        })
      )
      .subscribe();
  }
}
